import { useEffect, useRef, memo } from 'react';
import { useTheme } from '@/context/ThemeContext';

function TradingViewWidget({ symbol = 'BINANCE:BTCUSDT', height = 500, interval = '60' }) {
  const container = useRef();
  const { theme } = useTheme();
  useEffect(() => {
    if (!container.current) return;
    container.current.innerHTML = '';
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true, symbol, interval, timezone: 'Etc/UTC',
      theme, style: '1', locale: 'en',
      toolbar_bg: theme === 'dark' ? '#0D1117' : '#FFFFFF',
      enable_publishing: false, hide_top_toolbar: false,
      hide_legend: false, save_image: false, container_id: 'tv_' + symbol,
      backgroundColor: theme === 'dark' ? 'rgba(13, 17, 23, 1)' : 'rgba(255, 255, 255, 1)',
      gridColor: 'rgba(255,106,0,0.06)',
    });
    container.current.appendChild(script);
  }, [symbol, interval, theme]);
  return <div className="tradingview-widget-container rounded-lg overflow-hidden neon-border" ref={container} style={{ height, width: '100%' }} />;
}
export default memo(TradingViewWidget);

function useTVReload(configFactory, deps) {
  const ref = useRef();
  const { theme } = useTheme();
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = '';
    const s = document.createElement('script');
    s.src = configFactory(theme).src;
    s.async = true;
    s.innerHTML = JSON.stringify(configFactory(theme).config);
    ref.current.appendChild(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, ...(deps || [])]);
  return ref;
}

export function TVTickerTape() {
  const ref = useTVReload((theme) => ({
    src: 'https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js',
    config: {
      symbols: [
        { proName: 'FOREXCOM:SPXUSD', title: 'S&P 500' },
        { proName: 'BINANCE:BTCUSDT', title: 'BTC/USDT' },
        { proName: 'BINANCE:ETHUSDT', title: 'ETH/USDT' },
        { proName: 'FX:EURUSD', title: 'EUR/USD' },
        { proName: 'FX:GBPUSD', title: 'GBP/USD' },
        { proName: 'OANDA:XAUUSD', title: 'Gold' },
        { proName: 'NASDAQ:AAPL', title: 'Apple' },
        { proName: 'NASDAQ:TSLA', title: 'Tesla' },
      ],
      showSymbolLogo: true, colorTheme: theme, isTransparent: true, displayMode: 'adaptive', locale: 'en',
    },
  }));
  return <div ref={ref} className="tradingview-widget-container w-full" />;
}

export function TVHeatmap() {
  const ref = useTVReload((theme) => ({
    src: 'https://s3.tradingview.com/external-embedding/embed-widget-crypto-coins-heatmap.js',
    config: {
      dataSource: 'Crypto', blockSize: 'market_cap_calc', blockColor: '24h_close_change|5',
      locale: 'en', symbolUrl: '', colorTheme: theme, hasTopBar: false,
      isDataSetEnabled: false, isZoomEnabled: true, hasSymbolTooltip: true, width: '100%', height: 500,
    },
  }));
  return <div ref={ref} className="w-full rounded-lg overflow-hidden neon-border" />;
}

export function TVEconomicCalendar() {
  const ref = useTVReload((theme) => ({
    src: 'https://s3.tradingview.com/external-embedding/embed-widget-events.js',
    config: { colorTheme: theme, isTransparent: true, width: '100%', height: 500, locale: 'en', importanceFilter: '0,1' },
  }));
  return <div ref={ref} className="w-full rounded-lg overflow-hidden neon-border" />;
}

export function TVMarketOverview() {
  const ref = useTVReload((theme) => ({
    src: 'https://s3.tradingview.com/external-embedding/embed-widget-market-overview.js',
    config: {
      colorTheme: theme, dateRange: '12M', showChart: true, locale: 'en', width: '100%', height: 500,
      largeChartUrl: '', isTransparent: true, showSymbolLogo: true, showFloatingTooltip: true,
      tabs: [
        { title: 'Indices', symbols: [{ s: 'FOREXCOM:SPXUSD' }, { s: 'FOREXCOM:NSXUSD' }, { s: 'FOREXCOM:DJI' }] },
        { title: 'Forex', symbols: [{ s: 'FX:EURUSD' }, { s: 'FX:GBPUSD' }, { s: 'FX:USDJPY' }] },
        { title: 'Crypto', symbols: [{ s: 'BINANCE:BTCUSDT' }, { s: 'BINANCE:ETHUSDT' }, { s: 'BINANCE:SOLUSDT' }] },
      ],
    },
  }));
  return <div ref={ref} className="w-full rounded-lg overflow-hidden neon-border" />;
}
